import React, { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import AuthLayout from '../layouts/AuthLayout';
import BottomMenuLayout from '../layouts/BottomMenuLayout';
import { AppRouteEnum } from './routeEnums';

// Define component types to fix lazy loading type issues
type LoginComponent = React.ComponentType;
type RegisterComponent = React.ComponentType;
type ForgotPasswordComponent = React.ComponentType;

// Assets group
type AssetsComponent = React.ComponentType;

// Order group
type OrderComponent = React.ComponentType;

// History group
type HistoryComponent = React.ComponentType;

// Statement group
type StatementComponent = React.ComponentType;
type StockStatementComponent = React.ComponentType;
type WinLossStatementComponent = React.ComponentType;
type PortfolioStatementComponent = React.ComponentType;
type MoneyStatementComponent = React.ComponentType;
type MoneyTransactionDetailComponent = React.ComponentType;

// Menu group
type MenuComponent = React.ComponentType;
type SettingsComponent = React.ComponentType;
type ProfileComponent = React.ComponentType;
type ChangePasswordComponent = React.ComponentType;
type NotificationsComponent = React.ComponentType;
type NotificationDetailComponent = React.ComponentType;
type SmartOtpSetupComponent = React.ComponentType;
type SupportChatComponent = React.ComponentType;
type VerifySmartOTPComponent = React.ComponentType;
type BankAccountListComponent = React.ComponentType;
type BankAccountComponent = React.ComponentType;
type ProductsComponent = React.ComponentType;
type MoneyTransactionsComponent = React.ComponentType;
type DepositComponent = React.ComponentType;
type WithdrawComponent = React.ComponentType;
type InternalTransferComponent = React.ComponentType;
type ProductDetailComponent = React.ComponentType;
type AuthLayoutComponent = React.ComponentType;
type BottomMenuLayoutComponent = React.ComponentType;
type PortfolioComponent = React.ComponentType;

// Public components
const Login = lazy(() => import('../pages/Login') as Promise<{ default: LoginComponent }>);
const Register = lazy(() => import('../pages/Register') as Promise<{ default: RegisterComponent }>);
const ForgotPassword = lazy(() => import('../pages/ForgotPassword') as Promise<{ default: ForgotPasswordComponent }>);

// Assets group
const Assets = lazy(() => import('../pages/private/assets/Assets') as Promise<{ default: AssetsComponent }>);
const Portfolio = lazy(() => import('../pages/private/portfolio/Portfolio') as Promise<{ default: PortfolioComponent }>);

// Order group
const Order = lazy(() => import('../pages/private/order/Order') as Promise<{ default: OrderComponent }>);

// History group
const History = lazy(() => import('../pages/private/history/History') as Promise<{ default: HistoryComponent }>);

// Statement group
const Statement = lazy(() => import('../pages/private/statement/Statement') as Promise<{ default: StatementComponent }>);
const StockStatement = lazy(() => import('../pages/private/statement/StockStatement') as Promise<{ default: StockStatementComponent }>);
const WinLossStatement = lazy(() => import('../pages/private/statement/WinLossStatement') as Promise<{ default: WinLossStatementComponent }>);
const PortfolioStatement = lazy(() => import('../pages/private/statement/PortfolioStatement') as Promise<{ default: PortfolioStatementComponent }>);
const MoneyStatement = lazy(() => import('../pages/private/statement/MoneyStatement') as Promise<{ default: MoneyStatementComponent }>);

// Menu group
const Menu = lazy(() => import('../pages/private/menu/Menu') as Promise<{ default: MenuComponent }>);
const Settings = lazy(() => import('../pages/private/menu/setting/Setting') as Promise<{ default: SettingsComponent }>);
const Profile = lazy(() => import('../pages/private/menu/Profile') as Promise<{ default: ProfileComponent }>);
const BankAccountList = lazy(() => import('../pages/private/menu/BankAccountList') as Promise<{ default: BankAccountListComponent }>);
const BankAccount = lazy(() => import('../pages/private/menu/BankAccount') as Promise<{ default: BankAccountComponent }>);
const ChangePassword = lazy(() => import('../pages/private/menu/setting/ChangePassword') as Promise<{ default: ChangePasswordComponent }>);
const Notifications = lazy(() => import('../pages/private/menu/notification/Notifications') as Promise<{ default: NotificationsComponent }>);
// SmartOTP components - using the barrel file for cleaner imports
const SmartOtpSetup = lazy(() => import('../pages/private/smartotp').then(m => ({ default: m.SmartOtpSetup as SmartOtpSetupComponent })));
const SupportChat = lazy(() => import('../pages/private/support/SupportChat') as Promise<{ default: SupportChatComponent }>);
// Products component
const Products = lazy(() => import('../pages/private/products/Products') as Promise<{ default: ProductsComponent }>);
const ProductDetail = lazy(() => import('../pages/private/products/ProductDetail') as Promise<{ default: ProductDetailComponent }>);
// Money Transactions component
const MoneyTransactions = lazy(() => import('../pages/private/menu/money-transactions/MoneyTransactions') as Promise<{ default: MoneyTransactionsComponent }>);
const Deposit = lazy(() => import('../pages/private/money/Deposit') as Promise<{ default: DepositComponent }>);
const Withdraw = lazy(() => import('../pages/private/money/Withdraw') as Promise<{ default: WithdrawComponent }>);
const InternalTransfer = lazy(() => import('../pages/private/money/InternalTransfer') as Promise<{ default: InternalTransferComponent }>);

// Public routes configuration
export const publicRoutes: RouteObject[] = [
  {
    path: AppRouteEnum.LOGIN,
    element: <Login />,
  },
  {
    path: AppRouteEnum.REGISTER,
    element: <Register />,
  },
  {
    path: AppRouteEnum.FORGOT_PASSWORD,
    element: <ForgotPassword />,
  },
];

// Private routes configuration
export const privateRoutes: RouteObject[] = [
  {
    element: <AuthLayout />,
    children: [
      // Settings and Profile (no bottom menu)
      {
        path: AppRouteEnum.SETTINGS,
        element: <Settings />,
      },
      {
        path: AppRouteEnum.PROFILE,
        element: <Profile />,
      },
      {
        path: AppRouteEnum.CHANGE_PASSWORD,
        element: <ChangePassword />,
      },
      {
        path: AppRouteEnum.NOTIFICATIONS,
        element: <Notifications />,
      },
      {
        path: AppRouteEnum.SMART_OTP_SETUP,
        element: <SmartOtpSetup />,
      },
      {
        path: AppRouteEnum.SUPPORT_CHAT,
        element: <SupportChat />,
      },
      {
        path: AppRouteEnum.BANK_ACCOUNT_LIST,
        element: <BankAccountList />,
      },
      {
        path: AppRouteEnum.BANK_ACCOUNT_EDIT,
        element: <BankAccount />,
      },
      {
        path: AppRouteEnum.BANK_ACCOUNT_NEW,
        element: <BankAccount />,
      },
      {
        path: AppRouteEnum.PRODUCTS,
        element: <Products />,
      },
      {
        path: AppRouteEnum.PRODUCTS_DETAIL,
        element: <ProductDetail />,
      },
      {
        path: AppRouteEnum.MONEY_TRANSACTIONS,
        element: <MoneyTransactions />,
      },
      {
        path: AppRouteEnum.DEPOSIT,
        element: <Deposit />,
      },
      {
        path: AppRouteEnum.WITHDRAW,
        element: <Withdraw />,
      },
      {
        path: AppRouteEnum.INTERNAL_TRANSFER,
        element: <InternalTransfer />,
      },
    ],
  },
  {
    element: <BottomMenuLayout />,
    children: [
      // Assets group
      {
        path: AppRouteEnum.ASSETS,
        element: <Assets />,
      },
      {
        path: AppRouteEnum.PORTFOLIO,
        element: <Portfolio />,
      },

      // Order group
      {
        path: AppRouteEnum.ORDER,
        element: <Order />,
      },

      // History group
      {
        path: AppRouteEnum.HISTORY,
        element: <History />,
      },

      // Statement group
      {
        path: AppRouteEnum.STATEMENT,
        element: <Statement />,
      },
      {
        path: AppRouteEnum.STOCK_STATEMENT,
        element: <StockStatement />,
      },
      {
        path: AppRouteEnum.WINLOSS_STATEMENT,
        element: <WinLossStatement />,
      },
      {
        path: AppRouteEnum.PORTFOLIO_STATEMENT,
        element: <PortfolioStatement />,
      },
      {
        path: AppRouteEnum.MONEY_STATEMENT,
        element: <MoneyStatement />,
      },

      // Menu group
      {
        path: AppRouteEnum.MENU,
        element: <Menu />,
      },
    ],
  },
];
